let posthogInstance = null;
// Events captured before the SDK finishes its async init are queued here
// and flushed once posthogInstance is available.
let pendingEvents = [];
let pendingIdentify = null;
// Flag callbacks registered before init, attached to the SDK once it loads.
let pendingFlagCallbacks = [];
// Super properties set before init. `null` marks an unregister so a late init
// does not resurrect a property that was cleared in the meantime.
let pendingSuperProperties = {};

const PostHog = {
  /**
   * Initialize PostHog SDK.
   * @param {string} apiKey - PostHog project API key
   * @param {string} apiHost - PostHog API host URL
   */
  async init(apiKey, apiHost) {
    if (typeof window !== 'object') {
      return;
    }

    if (!apiKey || !apiHost) {
      return;
    }

    try {
      const module = await import('posthog-js');
      const posthog = module.default;
      posthogInstance = posthog;

      posthog.init(apiKey, {
        api_host: apiHost,
        // Manual pageview capture via tracker.pageView()
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: true,
        loaded: ph => {
          ph.debug(false);
        },
      });

      // Flush identify that arrived before the SDK was ready
      if (pendingIdentify) {
        const { userId, email, name } = pendingIdentify;
        posthog.identify(`${userId}`, {
          email: email || undefined,
          name: name || undefined,
        });
        pendingIdentify = null;
      }

      // Super properties must be in place before the queued events flush
      for (const [name, value] of Object.entries(pendingSuperProperties)) {
        if (value === null) {
          posthog.unregister(name);
        } else {
          posthog.register({ [name]: value });
        }
      }
      pendingSuperProperties = {};

      // Flush events that arrived before the SDK was ready
      for (const { eventName, properties, options } of pendingEvents) {
        posthog.capture(eventName, properties, options);
      }
      pendingEvents = [];

      for (const entry of pendingFlagCallbacks) {
        if (entry.cancelled) continue;
        entry.unsubscribe = posthog.onFeatureFlags(entry.callback);
        // The SDK invokes the callback during registration when flags are
        // already loaded, so a cancel from inside that call lands here.
        if (entry.cancelled) entry.unsubscribe();
      }
      pendingFlagCallbacks = [];
    } catch (_) {
      // silent
    }
  },

  pageView(properties = {}) {
    try {
      posthogInstance?.capture('$pageview', properties);
    } catch (_) {
      // silent
    }
  },

  identify(userId, email, name) {
    try {
      if (posthogInstance) {
        posthogInstance.identify(`${userId}`, {
          email: email || undefined,
          name: name || undefined,
        });
      } else {
        pendingIdentify = { userId, email, name };
      }
    } catch (_) {
      // silent
    }
  },

  reset() {
    pendingEvents = [];
    pendingIdentify = null;
    try {
      posthogInstance?.reset();
    } catch (_) {
      // silent
    }
  },

  customEvent(eventName, properties = {}, options) {
    try {
      if (posthogInstance) {
        posthogInstance.capture(eventName, properties, options);
      } else {
        pendingEvents.push({ eventName, properties, options });
      }
    } catch (_) {
      // silent
    }
  },

  /**
   * Attach a property to every subsequent event. Persisted by posthog-js
   * across page loads until `unregister` or `reset`.
   */
  register(properties = {}) {
    try {
      if (posthogInstance) {
        posthogInstance.register(properties);
      } else {
        Object.assign(pendingSuperProperties, properties);
      }
    } catch (_) {
      // silent
    }
  },

  unregister(propertyName) {
    try {
      if (posthogInstance) {
        posthogInstance.unregister(propertyName);
      } else {
        pendingSuperProperties[propertyName] = null;
      }
    } catch (_) {
      // silent
    }
  },

  /**
   * Run `callback(flags, variants)` whenever flags load or reload. The SDK
   * calls it immediately when flags are already loaded. Never fires if the
   * SDK fails to load, so callers need their own fallback timeout.
   * After init the immediate call happens before this returns, so a callback
   * cannot unsubscribe from inside it and needs its own run-once guard.
   * @returns {Function} unsubscribe
   */
  onFeatureFlags(callback) {
    try {
      if (posthogInstance) {
        return posthogInstance.onFeatureFlags(callback);
      }
      const entry = { callback, cancelled: false, unsubscribe: null };
      pendingFlagCallbacks.push(entry);
      return () => {
        entry.cancelled = true;
        pendingFlagCallbacks = pendingFlagCallbacks.filter(pending => pending !== entry);
        entry.unsubscribe?.();
      };
    } catch (_) {
      return () => undefined;
    }
  },

  isFeatureEnabled(flagKey) {
    try {
      return posthogInstance?.isFeatureEnabled(flagKey);
    } catch (_) {
      return undefined;
    }
  },

  getFeatureFlag(flagKey) {
    try {
      return posthogInstance?.getFeatureFlag(flagKey);
    } catch (_) {
      return undefined;
    }
  },
};

export default PostHog;
